import type { Architecture, CreateRelease, Platform, Release } from "@updaterelay/contracts";
import { useEffect, useState } from "react";
import { Modal } from "./Modal";

interface ReleaseDialogProps {
  open: boolean;
  release: Release | null;
  applicationName: string;
  busy: boolean;
  error?: string;
  onClose: () => void;
  onSave: (input: CreateRelease) => Promise<void>;
}

export function ReleaseDialog({
  open,
  release,
  applicationName,
  busy,
  error,
  onClose,
  onSave,
}: ReleaseDialogProps) {
  const [version, setVersion] = useState("");
  const [channel, setChannel] = useState("stable");
  const [platform, setPlatform] = useState<Platform>("any");
  const [architecture, setArchitecture] = useState<Architecture>("any");
  const [installerUrl, setInstallerUrl] = useState("");
  const [releaseNotes, setReleaseNotes] = useState("");
  const [mandatory, setMandatory] = useState(false);
  const [published, setPublished] = useState(false);

  useEffect(() => {
    if (!open) return;
    setVersion(release?.version ?? "");
    setChannel(release?.channel ?? "stable");
    setPlatform(release?.platform ?? "any");
    setArchitecture(release?.architecture ?? "any");
    setInstallerUrl(release?.installerUrl ?? "");
    setReleaseNotes(release?.releaseNotes ?? "");
    setMandatory(release?.mandatory ?? false);
    setPublished(Boolean(release?.publishedAt));
  }, [open, release]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      eyebrow={applicationName}
      title={release ? `Edit ${release.version}` : "Create release"}
    >
      <form
        className="form-stack"
        onSubmit={async (event) => {
          event.preventDefault();
          await onSave({
            version,
            channel,
            platform,
            architecture,
            installerUrl,
            releaseNotes: releaseNotes.trim() || null,
            mandatory,
            publishedAt: published ? (release?.publishedAt ?? new Date().toISOString()) : null,
          });
        }}
      >
        <div className="field-grid field-grid--three">
          <label className="field">
            <span>Version</span>
            <input
              required
              value={version}
              placeholder="1.4.0"
              pattern="[0-9]+\.[0-9]+\.[0-9]+.*"
              onChange={(event) => setVersion(event.target.value)}
            />
          </label>
          <label className="field">
            <span>Channel</span>
            <input
              required
              value={channel}
              placeholder="stable"
              pattern="[a-z0-9][a-z0-9._-]*"
              onChange={(event) => setChannel(event.target.value.toLowerCase())}
            />
          </label>
          <label className="field">
            <span>Platform</span>
            <select
              value={platform}
              onChange={(event) => setPlatform(event.target.value as Platform)}
            >
              <option value="any">Any platform</option>
              <option value="windows">Windows</option>
              <option value="macos">macOS</option>
              <option value="linux">Linux</option>
            </select>
          </label>
        </div>
        <div className="field-grid">
          <label className="field">
            <span>Architecture</span>
            <select
              value={architecture}
              onChange={(event) => setArchitecture(event.target.value as Architecture)}
            >
              <option value="any">Any architecture</option>
              <option value="universal">Universal</option>
              <option value="x64">x64</option>
              <option value="arm64">ARM64</option>
              <option value="x86">x86</option>
            </select>
          </label>
          <label className="field">
            <span>Installer URL</span>
            <input
              required
              type="url"
              value={installerUrl}
              placeholder="https://downloads.example.com/app-1.4.0.exe"
              onChange={(event) => setInstallerUrl(event.target.value)}
            />
          </label>
        </div>
        <label className="field">
          <span>Release notes</span>
          <textarea
            rows={4}
            maxLength={10_000}
            value={releaseNotes}
            placeholder="Summarize what changed for the update prompt."
            onChange={(event) => setReleaseNotes(event.target.value)}
          />
        </label>
        <div className="toggle-grid">
          <label className="toggle-card">
            <input
              type="checkbox"
              checked={published}
              onChange={(event) => setPublished(event.target.checked)}
            />
            <span>
              <strong>Publish immediately</strong>
              <small>Eligible clients can discover this release as soon as it is saved.</small>
            </span>
          </label>
          <label className="toggle-card">
            <input
              type="checkbox"
              checked={mandatory}
              onChange={(event) => setMandatory(event.target.checked)}
            />
            <span>
              <strong>Mandatory update</strong>
              <small>The client decides how to enforce this signal.</small>
            </span>
          </label>
        </div>
        {error ? <p className="form-error">{error}</p> : null}
        <div className="modal__actions">
          <button className="button button--ghost" type="button" onClick={onClose}>
            Cancel
          </button>
          <button className="button button--primary" type="submit" disabled={busy}>
            {busy ? "Saving…" : release ? "Save release" : "Create release"}
          </button>
        </div>
      </form>
    </Modal>
  );
}
